import {Popover, Transition} from "@headlessui/react";
import {ChevronDownIcon} from "@heroicons/react/24/outline";
import {BillingUserType, CountryType, UserType} from "data/types";
import {FC, Fragment, useEffect, useState} from "react";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import {useSaveBillingMutation} from "store/api";
import {getCountries, getCountryByDialCode} from "utils/getCountries";
import {ValidatorUtils} from "../../utils/validators";

const AddBilling: FC<{
  setIsOpened: React.Dispatch<React.SetStateAction<boolean>>;
  refresh: any;
}> = ({setIsOpened, refresh}) => {
  const [documentTypeState, setDocumentTypeState] = useState("Documento");
  const [nameState, setNameState] = useState("Nombre");
  const [emailState, setEmailState] = useState("Email");
  const [prefixState, setPrefixState] = useState("Prefijo");
  const [phoneState, setPhoneState] = useState("");
  const [phoneMaxLength, setPhoneMaxLength] = useState<number>(9);
  const [ciState, setCiState] = useState("");
  const [addressState, setAddressState] = useState("");
  const [saveBilling] = useSaveBillingMutation();
  const [alert, setAlert] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const countries: CountryType[] = getCountries();

  const [hasInteracted, setHasInteracted] = useState(false);
  const documentType = [
    {value: "CI", viewValue: "CI"},
    {value: "CE", viewValue: "CE"},
    {value: "PASSPORT", viewValue: "PASAPORTE"},
    {value: "RUC", viewValue: "RUC"},
  ];
  const userData = localStorage.getItem("user");

  let user: UserType;
  if (userData) {
    user = JSON.parse(userData) as UserType;
  }
  const getViewValue = (value: string, categories: any[]): string => {
    const found = categories.find((item) => item.value === value);
    return found ? found.viewValue : undefined;
  };

  const getValue = (viewValue: string, categories: any[]): string => {
    const found = categories.find((item) => item.viewValue === viewValue);
    return found ? found.value : undefined;
  };

  const save = async () => {
    setHasInteracted(true);
    setIsLoading(true);
    if (documentTypeState === "RUC") {
      const isRucValid = ValidatorUtils.validateEcuadorRUC(ciState);
      if (!isRucValid) {
        setIsLoading(false);
        setAlert("Ingrese un RUC válido");
        setTimeout(() => setAlert(null), 3000);
        return;
      }
    } else if (documentTypeState === "CI") {
      const isCIValid = ValidatorUtils.validatePersonalID(ciState);
      if (!isCIValid) {
        setIsLoading(false);
        setAlert("Ingrese un CI válido");
        setTimeout(() => setAlert(null), 3000);
        return;
      }
    }
    const body: BillingUserType = {
      address: addressState,
      documentType: getValue(documentTypeState, documentType),
      email: emailState,
      identification: ciState,
      name: nameState,
      phone: phoneState,
      prefix: `+${prefixState}`,
    };

    const {data, error} = await saveBilling(body);
    if (error) {
      const fetchError = error as {
        status: number;
        data: {error: string; statusMessage: string};
      };
      setIsLoading(false);
      if (fetchError.status === 409) {
        setAlert("Identificación duplicada");
        setTimeout(() => setAlert(null), 3000);
        return;
      }
    }
    if (data.meta.status === 201) {
      setIsLoading(false);
      setIsOpened(false);
      refresh();
    } else {
      setIsLoading(false);
      setIsOpened(false);
      setAlert("Ocurrió un error, intente nuevamente.");
      setTimeout(() => setAlert(null), 3000);
    }
  };

  const isFormValid =
    documentTypeState !== "Documento" &&
    ciState.trim() !== "" &&
    nameState.trim() !== "" &&
    emailState.trim() !== "" &&
    prefixState.trim() !== "" &&
    phoneState.trim() !== "" &&
    addressState.trim() !== "";

  useEffect(() => {
    const userDocument =
      user.document === "NIT"
        ? "PASSPORT"
        : user.document === "DNI"
        ? "PASSPORT"
        : user.document;

    setDocumentTypeState(
      user.document === ""
        ? "Documento"
        : getViewValue(userDocument, documentType)
    );
    setCiState(user.ci);
    setNameState(user.name);
    setEmailState(user.email);
    const prefix = user.prefix.replace("+", "") || "593";
    setPrefixState(prefix);
    setPhoneState(user.phone);
    setAddressState(user.address);
  }, []);

  return (
    <div
      className={` w-full flex flex-col items-center text-center sm:rounded-2xl sm:border border-neutral-200 dark:border-neutral-700 space-y-2 sm:space-y-2 px-0 sm:p-6 xl:p-8`}
    >
      <div className="flex items-center space-x-4 w-full justify-center">
        <Popover className="relative">
          {({open, close}) => (
            <>
              <Popover.Button
                className={`
           ${open ? "" : ""}
            px-4 py-1.5 rounded-md inline-flex items-center font-medium hover:text-opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 text-xs`}
                onClickCapture={() => document.querySelector("html")?.click()}
              >
                <span>{`${documentTypeState}`}</span>
                <ChevronDownIcon
                  className={`${
                    open ? "" : "text-opacity-70"
                  } ml-2 h-4 w-4 group-hover:text-opacity-80 transition ease-in-out duration-150`}
                  aria-hidden="true"
                />
              </Popover.Button>
              <Transition
                as={Fragment}
                enter="transition ease-out duration-200"
                enterFrom="opacity-0 translate-y-1"
                enterTo="opacity-100 translate-y-0"
                leave="transition ease-in duration-150"
                leaveFrom="opacity-100 translate-y-0"
                leaveTo="opacity-0 translate-y-1"
              >
                <Popover.Panel className="absolute z-20 w-screen max-w-[200px] sm:max-w-[220px] px-4 left-full mt-3 transform left-1/2 sm:px-0  ">
                  <div className="overflow-hidden rounded-2xl shadow-lg ring-1 ring-black/5 dark:ring-white/10 ">
                    <div className="relative grid gap-8 bg-white dark:bg-neutral-800 p-7 ">
                      {documentType.map((item: any) => (
                        <a
                          key={item.value}
                          href={item.value}
                          onClick={(e) => {
                            e.preventDefault();
                            setDocumentTypeState(item.viewValue);
                            close();
                          }}
                          className="flex items-center p-2 -m-3 transition duration-150 ease-in-out rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus-visible:ring focus-visible:ring-orange-500 focus-visible:ring-opacity-50"
                        >
                          <p className="text-sm font-medium ">
                            {item.viewValue}
                          </p>
                        </a>
                      ))}
                    </div>
                  </div>
                </Popover.Panel>
              </Transition>
            </>
          )}
        </Popover>
        <div className="flex items-center space-x-4 w-full justify-center">
          <input
            type="text"
            value={ciState}
            placeholder="Número de documento"
            onChange={(e) => setCiState(e.target.value)}
          />

          {hasInteracted && ciState === "" && (
            <p className="text-red-500 text-xs">{"*"}</p>
          )}
        </div>
      </div>
      <div className="flex items-center space-x-4 w-full justify-center">
        <i className="las la-user flex-shrink-0"></i>
        <input
          type="text"
          value={nameState}
          placeholder="Nombre"
          onChange={(e) => setNameState(e.target.value)}
        />

        {hasInteracted && nameState === "" && (
          <p className="text-red-500 text-xs">{"*"}</p>
        )}
        <i className="las la-envelope flex-shrink-0"></i>
        <input
          type="text"
          value={emailState}
          placeholder="Correo electrónico"
          onChange={(e) => setEmailState(e.target.value)}
        />

        {hasInteracted && emailState === "" && (
          <p className="text-red-500 text-xs">{"*"}</p>
        )}
      </div>
      <div className="flex items-center space-x-4 w-full justify-center">
        <i className="las la-flag flex-shrink-0"></i>
        <select
          key="prefix"
          value={prefixState}
          onChange={(e) => {
            setPrefixState(e.target.value);
            const country = getCountryByDialCode(e.target.value);
            setPhoneMaxLength(country!.maxLength);
            setPhoneState("");
          }}
        >
          <option value="Prefijo" disabled>
            Prefijo
          </option>
          {countries.map((country) => (
            <option
              key={country.dialCode + country.name}
              value={country.dialCode}
            >
              {country.flag} +{country.dialCode} {country.name}
            </option>
          ))}
        </select>
        <i className="las la-phone flex-shrink-0"></i>
        <input
          type="text"
          value={phoneState}
          onChange={(e) => {
            const value = e.target.value;
            if (/^\d*$/.test(value)) setPhoneState(value);
          }}
          maxLength={phoneMaxLength}
          inputMode="numeric"
          pattern="\d*"
        />
      </div>
      {hasInteracted && phoneMaxLength !== phoneState.length && (
        <p className="text-red-500 text-xs">{"El número no es válido"}</p>
      )}
      <div className="flex items-center space-x-4 w-full justify-center">
        <i className="las la-city flex-shrink-0"></i>
        <input
          type="text"
          value={addressState}
          placeholder="Dirección"
          onChange={(e) => setAddressState(e.target.value)}
          inputMode="text"
        />
        {hasInteracted && addressState === "" && (
          <p className="text-red-500 text-xs">{"*"}</p>
        )}
      </div>
      <div className="flex justify-end w-full">
        <ButtonPrimary
          className="mt-6"
          disabled={!isFormValid}
          loading={isLoading}
          onClick={() => save()}
        >
          Guardar
        </ButtonPrimary>
      </div>
      {alert && (
        <div className="fixed bottom-8 left-1/2 transform -translate-x-1/2 bg-red-600 text-white px-6 py-3 rounded-2xl shadow-2xl transition-all duration-300 ease-in-out opacity-90 hover:opacity-100 hover:scale-105 flex items-center gap-4">
          {alert}
        </div>
      )}
    </div>
  );
};
export default AddBilling;
